<script setup lang="ts">
import { computed } from 'vue'
import type { ShopReviewsData } from '~~/shared/types/store'
import StarRating from '~/components/common/StarRating.vue'

const props = defineProps<{ data: ShopReviewsData }>()

const visible = computed(() => props.data.available && props.data.count > 0)

const dateFmt = new Intl.DateTimeFormat('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })
function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : dateFmt.format(d)
}
</script>

<template>
  <section
    v-if="visible"
    id="opinie"
    class="shop-reviews container"
    data-testid="shop-reviews"
    aria-labelledby="shop-reviews-title"
  >
    <header class="shop-reviews__head">
      <h2 id="shop-reviews-title" class="shop-reviews__title">Opinie o sklepie</h2>
      <div class="shop-reviews__summary">
        <span class="shop-reviews__avg">{{ data.average.toFixed(1) }}/5</span>
        <StarRating :rating="data.average" size="lg" />
        <span class="shop-reviews__count">na podstawie {{ data.count }} opinii</span>
      </div>
      <p class="shop-reviews__source">
        Zweryfikowane opinie z Zaufane.pl —
        <a :href="data.profileUrl" target="_blank" rel="noopener noreferrer">
          zobacz wszystkie opinie
        </a>
      </p>
    </header>

    <ul class="shop-reviews__list">
      <li
        v-for="(review, i) in data.reviews"
        :key="i"
        class="shop-reviews__item"
        data-testid="review-item"
      >
        <div class="shop-reviews__item-head">
          <StarRating :rating="review.rating" size="sm" />
          <time v-if="review.date" :datetime="review.date" class="shop-reviews__date">
            {{ formatDate(review.date) }}
          </time>
        </div>
        <p class="shop-reviews__text">{{ review.text }}</p>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.shop-reviews { margin: 3rem auto; }
.shop-reviews__title { font-size: 1.5rem; margin-bottom: 1rem; }
.shop-reviews__summary { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.shop-reviews__avg { font-size: 2rem; font-weight: 700; }
.shop-reviews__count { color: var(--color-text-muted); }
.shop-reviews__source { margin: 0.5rem 0 1.5rem; font-size: 0.9rem; color: var(--color-text-muted); }
.shop-reviews__list { list-style: none; padding: 0; display: grid; gap: 1rem; }
.shop-reviews__item { padding: 1rem; border: 1px solid var(--color-border); border-radius: 8px; }
.shop-reviews__item-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.5rem; }
.shop-reviews__date { color: var(--color-text-muted); font-size: 0.85rem; }
.shop-reviews__text { margin: 0; }
</style>
